import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { OrderView } from '../../../services/orders/orders.service';
import type { OrderlineView } from '../../../services/orderlines/orderlines.service';
import type { ProductView } from '../../../services/products/products.service';
import type { CategoryView } from '../../../services/categories/categories.service';
import type { ComboSelectionRoute } from '../../../utils/orderline-selections';
import { printersService } from '../../../services/printers/printers.service';
import { kdsService } from '../../../services/kds/kds.service';
import { KdsPrintTicket } from '../../KdsPage/components/print/KdsPrintTicket';
import { captureKdsReceiptSnapshot } from '../../KdsPage/utils/kds-page';
import kdsPageClasses from '../../KdsPage/styles/kds-page.module.scss';
import {
  buildKitchenPrintGroups,
  resolveKitchenGroupLabels,
  type KitchenPrintGroup,
} from '../components/orders/section/kitchen-print-groups.util';
import { logToBackend } from '../../../services/client-log/client-log.service';

type ResolveComboRoute = (productId: number, itemId: number) => ComboSelectionRoute | null;
type ResolvedGroup = { group: KitchenPrintGroup; printerName: string };
type HeadlessJob = {
  order: OrderView;
  groups: ResolvedGroup[];
  actionGroupId: string;
  printTime: Date;
  key: string;
};

const MULTI_PRINTER_STAGGER_MS = 500;

// Take-away green-fire ticket copy; mirrors KdsPage labels with the 'לקחת' dining type.
const takeAwayLabels = {
  newBadge: 'חדש',
  orderNumber: 'מספר הזמנה',
  time: 'שעה',
  waiter: 'מלצר',
  diners: 'סועדים',
  placeholder: '--',
  table: 'שולחן',
  diningType: 'לקחת',
};

// Auto-prints the take-away green-fire bon on-device at close using the frontend KdsPrintTicket.
export function useTakeAwayKitchenAutoPrint(tenantId: string, employeeSession: string | null, currentEmployeeName: string | null) {
  const offscreenRefs = useRef<Array<HTMLDivElement | null>>([]);
  const inFlightRef = useRef<Set<string>>(new Set());
  const [headlessJob, setHeadlessJob] = useState<HeadlessJob | null>(null);

  const requestTakeAwayKitchenPrint = useCallback(
    async (
      order: OrderView,
      lines: OrderlineView[],
      posProducts: ProductView[],
      categories: CategoryView[],
      resolveComboSelectionRoute?: ResolveComboRoute,
    ) => {
      if (!employeeSession) {
        logToBackend('take-away-kitchen-print', 'request skipped: missing employee session', {
          orderId: order.id,
          orderCode: order.orderCode,
          lineCount: lines.length,
          productCount: posProducts.length,
        }, 'warn');
        return;
      }
      const key = String(order.id);
      logToBackend('take-away-kitchen-print', 'request received', {
        orderId: order.id,
        orderCode: order.orderCode,
        lineCount: lines.length,
        productCount: posProducts.length,
        employeeSessionPresent: true,
      }, 'info');
      if (inFlightRef.current.has(key)) {
        logToBackend('take-away-kitchen-print', 'request skipped: already in flight', {
          orderId: order.id,
          orderCode: order.orderCode,
          key,
        }, 'warn');
        return;
      }
      inFlightRef.current.add(key);

      try {
        const productById = new Map(posProducts.map((product) => [product.id, product]));
        const { groups, deliverableCount, undeliverableNames, unroutedComboWarning } = buildKitchenPrintGroups({
          lines,
          posProducts,
          categories,
          resolveComboSelectionRoute,
        });
        const lineRoutingSnapshot = lines.map((line) => {
          const product = line.productId != null ? productById.get(line.productId) : null;
          return {
            orderlineId: line.id,
            productId: line.productId,
            productName: line.productName,
            quantity: line.quantity,
            status: line.status,
            kdsEnabled: product?.kds === true,
            configuredPrinterIds: product?.printerIds ?? [],
          };
        });
        logToBackend('take-away-kitchen-print', 'route build complete', {
          orderId: order.id,
          orderCode: order.orderCode,
          lineCount: lines.length,
          deliverableCount,
          groupCount: groups.length,
          printerIds: groups.map((group) => group.printerId),
          groupItems: groups.map((group) => ({
            printerId: group.printerId,
            orderlineIds: group.orderlineIds,
            items: group.groupLines.map((entry) => ({
              productName: entry.productName,
              quantity: entry.quantity,
            })),
          })),
          undeliverableNames,
          unroutedComboWarning,
          lineRoutingSnapshot,
        }, groups.length > 0 ? 'info' : 'warn');
        if (unroutedComboWarning || undeliverableNames.length > 0 || (deliverableCount > 0 && groups.length === 0)) {
          logToBackend('take-away-kitchen-print', 'kitchen route resolution', {
            orderId: order.id,
            lineCount: lines.length,
            deliverableCount,
            groupCount: groups.length,
            undeliverableNames,
            unroutedComboWarning,
          }, 'warn');
        }
        if (groups.length === 0) {
          logToBackend('take-away-kitchen-print', 'request finished: no printer groups', {
            orderId: order.id,
            orderCode: order.orderCode,
            deliverableCount,
            undeliverableNames,
          }, 'warn');
          inFlightRef.current.delete(key);
          return;
        }
        const printers = await printersService.listActive(tenantId, employeeSession);
        const nameMap = new Map(printers.map((printer) => [printer.id, printer.name]));
        const resolved: ResolvedGroup[] = groups.map((group) => ({
          group,
          printerName: nameMap.get(group.printerId) ?? `Printer #${group.printerId}`,
        }));
        // Deterministic per-order id so the backend's single-winner claim collapses the same
        // close bon fired concurrently by multiple devices (initiator + resolved-SSE recovery).
        const actionGroupId = `ta-close-${order.id}`;
        logToBackend('take-away-kitchen-print', 'job prepared', {
          orderId: order.id,
          orderCode: order.orderCode,
          actionGroupId,
          resolvedGroups: resolved.map((entry) => ({
            printerId: entry.group.printerId,
            printerName: entry.printerName,
            orderlineIds: entry.group.orderlineIds,
            items: entry.group.groupLines.map((line) => ({
              productName: line.productName,
              quantity: line.quantity,
            })),
          })),
        }, 'info');
        setHeadlessJob({ order, groups: resolved, actionGroupId, printTime: new Date(), key });
      } catch (err) {
        inFlightRef.current.delete(key);
        console.error('Take-away kitchen auto-print failed:', err);
        logToBackend('take-away-kitchen-print', 'request failed before queue', {
          orderId: order.id,
          orderCode: order.orderCode,
          message: err instanceof Error ? err.message : String(err),
        }, 'error');
      }
    },
    [tenantId, employeeSession],
  );

  useEffect(() => {
    if (!headlessJob || !employeeSession) return;
    let cancelled = false;
    const job = headlessJob;
    void (async () => {
      try {
        logToBackend('take-away-kitchen-print', 'job started', {
          orderId: job.order.id,
          orderCode: job.order.orderCode,
          actionGroupId: job.actionGroupId,
          groupCount: job.groups.length,
        }, 'info');
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (process.env.NODE_ENV !== 'test') {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        if (cancelled) return;
        for (let i = 0; i < job.groups.length; i += 1) {
          const { group, printerName } = job.groups[i];
          const node = offscreenRefs.current[i];
          logToBackend('take-away-kitchen-print', 'group processing started', {
            orderId: job.order.id,
            orderCode: job.order.orderCode,
            actionGroupId: job.actionGroupId,
            groupIndex: i,
            printerId: group.printerId,
            printerName,
            orderlineIds: group.orderlineIds,
            hasRenderNode: Boolean(node),
          }, 'info');
          const image = process.env.NODE_ENV === 'test'
            ? 'data:image/jpeg;base64,test'
            : (node ? await captureKdsReceiptSnapshot(node) : undefined);
          if (cancelled) return;
          if (!image) {
            logToBackend('take-away-kitchen-print', 'group processing skipped: no printable image', {
              orderId: job.order.id,
              orderCode: job.order.orderCode,
              actionGroupId: job.actionGroupId,
              groupIndex: i,
              printerId: group.printerId,
              printerName,
              hasRenderNode: Boolean(node),
            }, 'error');
            continue;
          }
          const { logPrinterName, logItems } = resolveKitchenGroupLabels(group, printerName);
          try {
            logToBackend('take-away-kitchen-print', 'queueOrderPrint request', {
              orderId: job.order.id,
              orderCode: job.order.orderCode,
              actionGroupId: job.actionGroupId,
              groupIndex: i,
              printerId: group.printerId,
              printerName,
              orderlineIds: group.orderlineIds,
              logPrinterName,
            }, 'info');
            await kdsService.queueOrderPrint(
              tenantId,
              job.order.id,
              group.printerId,
              employeeSession,
              undefined,
              group.orderlineIds,
              job.actionGroupId,
              logPrinterName,
              logItems,
              image,
            );
            logToBackend('take-away-kitchen-print', 'queueOrderPrint success', {
              orderId: job.order.id,
              orderCode: job.order.orderCode,
              actionGroupId: job.actionGroupId,
              groupIndex: i,
              printerId: group.printerId,
              printerName,
              orderlineIds: group.orderlineIds,
            }, 'info');
          } catch (err) {
            console.error('Take-away kitchen print queue failed:', err);
            logToBackend('take-away-kitchen-print', 'queueOrderPrint failed', {
              orderId: job.order.id,
              printerId: group.printerId,
              printerName,
              orderlineIds: group.orderlineIds,
              message: err instanceof Error ? err.message : String(err),
            }, 'error');
            notifications.show({ color: 'red', title: 'ההדפסה נכשלה', message: `שגיאה בשליחת ההזמנה למטבח (${printerName})`, autoClose: 3000 });
          }
          if (i < job.groups.length - 1 && process.env.NODE_ENV !== 'test') {
            await new Promise((resolve) => setTimeout(resolve, MULTI_PRINTER_STAGGER_MS));
          }
        }
      } finally {
        inFlightRef.current.delete(job.key);
        logToBackend('take-away-kitchen-print', 'job finished', {
          orderId: job.order.id,
          orderCode: job.order.orderCode,
          actionGroupId: job.actionGroupId,
          groupCount: job.groups.length,
          cancelled,
        }, cancelled ? 'warn' : 'info');
        if (!cancelled) {
          setHeadlessJob(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [headlessJob, tenantId, employeeSession]);

  const kitchenPrintElements = headlessJob ? (
    <div aria-hidden="true" className={kdsPageClasses.printCaptureOffscreen}>
      {headlessJob.groups.map(({ group, printerName }, index) => (
        <KdsPrintTicket
          key={index}
          isTakeAway
          printerName={printerName}
          tableNumber={headlessJob.order.orderCode}
          orderNumber={headlessJob.order.orderCode}
          dinersCount={Number(headlessJob.order.dinersCount) || 1}
          printTime={headlessJob.printTime}
          waiterName={currentEmployeeName ?? headlessJob.order.openedByName ?? undefined}
          locale="he-IL"
          labels={takeAwayLabels}
          orderlines={group.groupLines}
          receiptPaperRef={(el) => { offscreenRefs.current[index] = el; }}
        />
      ))}
    </div>
  ) : null;

  return { requestTakeAwayKitchenPrint, kitchenPrintElements };
}
